import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { Image, makeStyles } from '@fluentui/react-components';

import {
  Collapse,
  useCollapseState,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  ImageListPortalContainerWithControlErrorHandling,
  ImageListSummaryCardDetails,
  PerfMaxImageSuggestionCarousel
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { ValidatedCollapseCard, FormCardBasicHeader } from '@bingads-webui-campaign-react/validated-form-cards';
import { FormCardBody, FormCardSummary } from '@bingads-webui-fluent-component/form-cards';

import ImageAssetsImg from '../media/image-icon.svg';
import { useDisabledKeynavRegion } from '../hooks/use-disabled-keynav-region';

const useStyles = makeStyles({
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
});

export const CollapsibleImages = ({
  images,
  imagesContainerRef,
  imageExperiencePortalContainerState,
  imageExperienceAddNewImageCallback,
  maxUploadNum,
  disabled,
}) => {
  const i18n = useContext(I18nContext);
  const MessageBarText = i18n.getString(_TL_('Select the add image icon above to find relevant images or generate custom AI images for your ads.'));

  const ref = useRef(null);

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(['images'], ['images']);

  const styles = useStyles();

  useDisabledKeynavRegion({ ref, disabled });

  return (
    <Collapse
      allPanelItems={allSections}
      checkItems
      skipItemsTypeCheck
      activeKeys={expandedSections}
      setActiveKeys={setExpandedSections}
    >
      <ValidatedCollapseCard
        key="images"
        className="images-card"
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <FormCardBasicHeader
          title={i18n.getString(_TL_('Images'))}
          helpTopic="pop_BA_LinkedIn_Images"
          helpLabel={i18n.getString(_TL_('Click to learn more about images'))}
          contentBefore={<Image src={ImageAssetsImg} aria-hidden />}
        />
        <FormCardBody>
          <span>{i18n.getString(_TL_('Add one image per ad.'))}</span>
          <div
            ref={ref}
            className={disabled ? styles.disabled : ''}
          >
            <ImageListPortalContainerWithControlErrorHandling
              i18n={i18n}
              containerRef={imagesContainerRef}
              containerState={imageExperiencePortalContainerState}
              isLogos={false}
            />
            <PerfMaxImageSuggestionCarousel i18n={i18n} addImageCallback={imageExperienceAddNewImageCallback} maxUploadNum={maxUploadNum} customMessageBarText={MessageBarText} />
          </div>
        </FormCardBody>
        <FormCardSummary>
          <ImageListSummaryCardDetails i18n={i18n} images={images} isLogos={false} />
        </FormCardSummary>
      </ValidatedCollapseCard>
    </Collapse>
  );
};

CollapsibleImages.propTypes = {
  images: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))).isRequired,
  imagesContainerRef: PropTypes.shape({}).isRequired,
  imageExperiencePortalContainerState: PropTypes.shape({}).isRequired,
  imageExperienceAddNewImageCallback: PropTypes.func.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
};

CollapsibleImages.defaultProps = {
  disabled: false,
};
